import * as React from 'react';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import {CollapsibleCard} from 'shells/card/collapsible-card';
import {Body} from 'shells/typography/body';

import type {FormFieldSubfieldsInput as Subfield} from 'nutshell-graphql-types';

import {
    entityTypeOptions,
    translateSchemaEntityTypeToGraphQL,
    getFieldMappingLabel,
} from '../../../helpers';
import type {
    InputFormField,
    FormFieldRowForDuplicateHelpers,
    EntityTypeOption,
    EntityTypeFromApi,
} from '../../../types';

import type {FormFieldSchema} from '../../../../core/nutshell-fields/types';

import {AddressTypeSelector} from '../separate-fields/address-type-selector';

import {AdditionalRules} from './additional-rules/additional-rules';
import {CompanyNameField} from './company-name-field';
import {EntityTypeMapper} from './entity-type-mapper';
import {getIconColor} from './helpers';
import {PersonNameMapper} from './person-name-mapper';
import {UneditableField} from './uneditable-field';

export type Props = {
    control: Control;
    name: string;
    watch: Watch;
    field: InputFormField;
    otherFieldsOnForm: FormFieldRowForDuplicateHelpers[];
    formSchema: FormFieldSchema;
    formId: string;
    onEditNameField: (updatedFieldName: string) => void;
    onEditEmailField: (updatedEntityName: string) => void;
    onEditAddressMultiFields: (
        updatedFields?: Subfield[],
        updatedIsSeparateFields?: boolean
    ) => void;
    preventEditing?: boolean;
    fields: FormFieldRowForDuplicateHelpers[];
    wrapInCard?: boolean;
};

export function FieldMapping(props: Props) {
    const {wrapInCard = true, ...restProps} = props;

    const entityType = restProps.watch(`${restProps.name}.entityType`);
    const fieldName = restProps.watch(`${restProps.name}.fieldName`);

    const entityTypeValue = entityTypeOptions.find((option) => option.value === entityType);

    const getFilteredEntityTypeOptions = (entityTypes: EntityTypeFromApi[]): EntityTypeOption[] => {
        const types = entityTypes.map((type) => translateSchemaEntityTypeToGraphQL(type));

        return entityTypeOptions.filter((option) => types.includes(option.value));
    };

    const fieldKey: string = restProps.field.attributeId || fieldName;
    const fieldSchema = restProps.formSchema.properties[fieldKey];
    const label = getFieldMappingLabel(restProps.formSchema, fieldKey);

    const isUneditableField = restProps.preventEditing || fieldSchema?.isCustomField === true;
    const isCompanyNameField = fieldKey === 'name' && entityType === 'ACCOUNT';
    const isPersonNameField =
        (!isUneditableField && fieldKey === 'name' && entityType === 'CONTACT') ||
        fieldKey === 'firstName' ||
        fieldKey === 'lastName';
    const filteredEntityTypeOptions = getFilteredEntityTypeOptions(fieldSchema?.entityTypes ?? []);

    // @ts-expect-error
    const getMappingComponent = (control, onEditNameField, onEditEmailField, otherFieldsOnForm) => {
        if (entityTypeValue && (filteredEntityTypeOptions.length === 1 || isUneditableField)) {
            return (
                <UneditableField
                    label={label}
                    showExplanatoryText={restProps.preventEditing || false}
                />
            );
        }

        if (isCompanyNameField) {
            return <CompanyNameField />;
        }

        if (isPersonNameField) {
            return (
                <PersonNameMapper
                    control={control}
                    name={restProps.name}
                    // @ts-expect-error
                    fieldSchema={fieldSchema}
                    fieldLabel={label}
                    fieldName={fieldName}
                    otherFieldsOnForm={otherFieldsOnForm}
                    onEditNameField={onEditNameField}
                />
            );
        }

        return (
            <EntityTypeMapper
                control={control}
                name={restProps.name}
                // @ts-expect-error
                fieldSchema={fieldSchema}
                fieldLabel={label}
                entityType={entityType}
                entityTypeOptions={filteredEntityTypeOptions}
                onEditEmailField={onEditEmailField}
            />
        );
    };

    const entityTypeOption = filteredEntityTypeOptions.find(
        (option) => option.value === entityType
    );
    const Icon = entityTypeOption && entityTypeOption.icon;

    const [isOpen, setIsOpen] = React.useState<boolean>(true);

    const inner = (
        <>
            <div className='pt-16'>
                <Body isBold={true} color='dark' size='small'>
                    Mapping
                </Body>
            </div>
            {getMappingComponent(
                restProps.control,
                restProps.onEditNameField,
                restProps.onEditEmailField,
                restProps.otherFieldsOnForm
            )}
            {fieldName === 'address' && (
                <div className='flex flex-dir-col gap-8'>
                    <Body isBold={true} color='dark' size='small'>
                        Address display
                    </Body>
                    <AddressTypeSelector
                        // @ts-expect-error
                        field={restProps.field}
                        fields={restProps.otherFieldsOnForm}
                        name={restProps.name}
                        control={restProps.control}
                        watch={restProps.watch}
                        onEditAddressMultiFields={restProps.onEditAddressMultiFields}
                        useSmallCheckboxes={true}
                        useSmallRadioButtons={true}
                    />
                </div>
            )}
            {(fieldSchema?.type === 'enum' || fieldSchema?.type === 'enum-multiple') &&
                fieldSchema && (
                    <AdditionalRules
                        formId={restProps.formId}
                        fieldSchema={fieldSchema}
                        currentField={restProps.field}
                        entityType={entityType}
                        name={restProps.name}
                        watch={restProps.watch}
                        control={restProps.control}
                    />
                )}
        </>
    );

    if (!wrapInCard) {
        return inner;
    }

    return (
        <CollapsibleCard
            header={
                <Body color='dark' size='large' isBold={true}>
                    Field
                </Body>
            }
            subheader={
                <div className='flex gap-4'>
                    {Icon && (
                        <Icon
                            size={12}
                            color={getIconColor(entityType)}
                            className='flex align-center'
                        />
                    )}
                    <Body color='light'>{isCompanyNameField ? 'Company name' : label}</Body>
                </div>
            }
            padding={16}
            hasBorder={true}
            hasShadow={true}
            overflowVisible={true}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            fitContent={false}
            fitHeight={false}
        >
            {inner}
        </CollapsibleCard>
    );
}
